import { FormDatepickerField, FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import useSubmitNew from 'shared/hooks/useSubmitNew';
import MassActionModalLayout from 'shared/layouts/massActionModalLayout';
import { MassActionItemProps, UseMassActionProps } from 'shared/types';
import { isNotPastDate, isValidDate } from 'shared/utility/dataGrid';
import { MassActionForm } from 'types/backend';
import { StartDateValidation } from './ChangeBySetValue';
import { FutureDateInfoBarProps } from './FutureDateInfoBar';

export type ChangeByDollarAmountActionForm = {
    amountIncrease: string;
    start?: string;
    reason?: string;
    comments: string;
    priceType?: string;
};
export type ChangeByDollarAmountForm = MassActionForm<ChangeByDollarAmountActionForm>;
export type InfoBar = {
    InfoBar?: ({ startDateFormValue }: FutureDateInfoBarProps) => JSX.Element | null;
};

type ChangeByDollarAmountProps<T> = MassActionItemProps<ChangeByDollarAmountForm, T, ChangeByDollarAmountActionForm> &
    StartDateValidation &
    InfoBar;

export default function ChangeByDollarAmount<T>({
    defaultValues,
    onSubmit,
    isLoading,
    tableDataContext,
    rowCount,
    columnConfig,
    allRowsSelected,
    validate,
    startDateValidation,
    InfoBar,
    canSetStartDate,
}: ChangeByDollarAmountProps<T>) {
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm<ChangeByDollarAmountForm>({
        defaultValues,
    });

    return (
        <MassActionModalLayout
            isLoading={isLoading}
            allRowsSelected={allRowsSelected}
            tableDataContext={tableDataContext}
            columnConfig={columnConfig}
            rowCount={rowCount}
            submitHandler={handleSubmit(onSubmit)}
        >
            <FormNumberField
                name={'actionForm.amountIncrease'}
                label='Amount increase'
                control={control}
                rules={{
                    required: 'Amount increase is required',
                    validate,
                    maxLength: { value: 10, message: `Maximum 10 characters` },
                }}
                fieldError={errors?.['actionForm']?.['amountIncrease']}
                type='money'
            />
            <FormDatepickerField
                name={'actionForm.start'}
                control={control}
                label='Start Date'
                rules={{
                    required: 'Start date is required',
                    validate: startDateValidation
                        ? { isValidDate, isNotPastDate, ...startDateValidation }
                        : { isValidDate, isNotPastDate },
                }}
                fieldError={errors?.['actionForm']?.['start']}
                disabled={!canSetStartDate}
            />
            <FormSelect
                name={'actionForm.reason'}
                label='Reason For Change'
                control={control}
                rules={{ required: 'Reason for change is required' }}
                options={[
                    { value: 'Volume Growth', displayText: 'Volume Growth' },
                    { value: 'Competition', displayText: 'Competition' },
                    { value: 'Resin Change', displayText: 'Resin Change' },
                    { value: 'Other', displayText: 'Other' },
                ]}
                fieldError={errors?.['actionForm']?.['reason']}
            />
            <FormTextField
                name={'actionForm.comments'}
                label='Reason Notes'
                control={control}
                fieldError={errors?.['actionForm']?.['comments']}
            />
            {InfoBar && <InfoBar startDateFormValue={watch('actionForm.start')} />}
        </MassActionModalLayout>
    );
}

export function useChangeByDollarAmountMassAction<T>({
    controllerName,
    affectedDataContext,
    selected,
    allRowsSelected,
    onSuccess,
}: UseMassActionProps<T>) {
    const { filtersApplied } = useContext(affectedDataContext);

    const changeByDollarAmountMapper = (form: ChangeByDollarAmountForm) => ({
        ...form,
        filters: filtersApplied,
        selected,
        allRowsSelected,
        type: 'Change market price by dollar amount',
    });

    return useSubmitNew({
        name: `${controllerName}/massaction/changebydollaramount`,
        mapper: changeByDollarAmountMapper,
        context: affectedDataContext,
        onSuccess: onSuccess,
    });
}
